import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';

const AuthContext = createContext(null);
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/auth/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password) => {
    const data = await api.post('/auth/login', { username, password });
    setUser(data.user);
    if (data.xp_event && data.xp_event.total_xp_gained > 0) {
      window.__pendingXpEvent = data.xp_event;
    }
    window.__pendingHoursToast = true;
    return data.user;
  };

  const register = async (username, password, confirmPassword, inviteCode) => {
    const data = await api.post('/auth/register', { username, password, confirmPassword, inviteCode });
    setUser(data.user);
    return data.user;
  };

  const logout = useCallback(async () => {
    setUser(null);  // immediately stops polls and blocks protected routes
    await api.post('/auth/logout').catch(() => {});
    window.location.replace('/login');
  }, []);

  // Auto-logout after 15 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        await api.post('/auth/logout').catch(() => {});
        window.location.replace('/login');
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data);
    } catch { /* ignore */ }
  }, []);

  // Poll /me every 5s so XP toasts fire on next refresh after purchase/achievement
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      api.get('/auth/me').then(setUser).catch(() => {
        // Session expired server-side — hard reload to login
        window.location.replace('/login');
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // Refresh immediately when returning to the app (tab focus / phone screen resume)
  // A 401 here means the server expired the session due to inactivity — redirect to login
  useEffect(() => {
    if (!user) return;
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        api.get('/auth/me').then(setUser).catch(() => {
          window.location.replace('/login');
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
